// Client Pages — a per-account (person or org) landing screen that ties
// together contact info, notes, linked properties, and open/completed WOs.
// Two routes: /admin/clients (list) and /admin/clients/:id (detail).

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Buildings, Phone, Envelope, Plus, User as UserIcon,
  MagnifyingGlass, X, Trash,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

export default function AdminClientPages() {
  const nav = useNavigate();
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", org_name: "", role: "", is_org: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/client-pages");
      setPages(r.data.pages || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = pages.filter(p => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(t) ||
      (p.org_name || "").toLowerCase().includes(t) ||
      (p.role || "").toLowerCase().includes(t)
    );
  });

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      const r = await api.post("/client-pages", form);
      toast.success("Page created");
      setCreating(false);
      setForm({ name: "", org_name: "", role: "", is_org: false });
      nav(`/admin/clients/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="client-pages-list">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-950 text-white p-6 sm:p-8 mb-5">
          <div className="flex items-center gap-3 mb-1">
            <Users weight="fill" size={28}/>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">
              Client Pages
            </h1>
          </div>
          <p className="mt-2 text-sm text-white/80 max-w-xl">
            One page per person or org — contact info, notes, linked properties, and live view of every open + completed WO on their book.
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-1 border-2 border-slate-300 bg-white h-10 px-2 flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={14} className="text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search name / org / role"
              data-testid="client-pages-search"
              className="h-full flex-1 outline-none text-sm"/>
            {q && <button onClick={() => setQ("")}><X size={14} className="text-slate-400"/></button>}
          </div>
          <button
            onClick={() => setCreating(true)}
            data-testid="new-client-btn"
            className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
            <Plus size={13}/> New Page
          </button>
        </div>

        {/* Inline create form */}
        {creating && (
          <div className="mb-4 p-4 border-2 border-emerald-300 bg-emerald-50" data-testid="client-page-create-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Name (person or org)"
                data-testid="new-client-name"
                className="h-9 px-2 border-2 border-slate-300 text-sm"/>
              <input value={form.org_name} onChange={e => setForm({...form, org_name: e.target.value})}
                placeholder="Organization (if a person)"
                data-testid="new-client-org"
                className="h-9 px-2 border-2 border-slate-300 text-sm"/>
              <input value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                placeholder="Role / relationship"
                data-testid="new-client-role"
                className="h-9 px-2 border-2 border-slate-300 text-sm"/>
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-700">
              <input type="checkbox" checked={form.is_org}
                onChange={e => setForm({...form, is_org: e.target.checked})}
                data-testid="new-client-is-org"/>
              This is an organization (not a person)
            </label>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={create} data-testid="save-new-client"
                className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[11px] tracking-widest">
                Create
              </button>
              <button onClick={() => setCreating(false)}
                className="h-9 px-4 border-2 border-slate-300 text-slate-700 font-black uppercase text-[11px] tracking-widest hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <div className="mt-6 text-center text-slate-500 text-xs font-bold uppercase">Loading…</div>}

        {!loading && filtered.length === 0 && (
          <div className="mt-8 border-2 border-dashed border-slate-300 rounded-lg p-10 text-center">
            <Users size={40} className="mx-auto text-slate-300"/>
            <div className="mt-3 font-black uppercase text-sm text-slate-500">
              {pages.length === 0 ? "No client pages yet" : "No matches"}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {pages.length === 0
                ? "Create one for each key person or org you work with — Mario, Adam, LaSalle Life Services, Leslie…"
                : "Try a different search term."}
            </div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="client-pages-grid">
            {filtered.map(p => (
              <Link key={p.id} to={`/admin/clients/${p.id}`}
                data-testid={`client-page-card-${p.id}`}
                className="block bg-white border-2 border-slate-200 hover:border-emerald-500 hover:shadow-md transition p-4 rounded group">
                <div className="flex items-start gap-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    p.is_org ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {p.is_org ? <Buildings weight="fill" size={18}/> : <UserIcon weight="fill" size={18}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900 truncate">{p.name}</div>
                    {p.org_name && !p.is_org && (
                      <div className="text-xs text-slate-500 truncate">{p.org_name}</div>
                    )}
                    {p.role && <div className="text-[11px] text-slate-500 truncate italic">{p.role}</div>}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Buildings size={11}/> {(p.linked_property_keys || []).length} props
                  </span>
                  {p.phone && (
                    <span className="inline-flex items-center gap-1"><Phone size={11}/></span>
                  )}
                  {p.email && (
                    <span className="inline-flex items-center gap-1"><Envelope size={11}/></span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
